import { PermissionCode, type AssetListQuery, type AssetListSortBy } from '@gb-ticket/shared-types';
import { ChevronLeftIcon, ChevronRightIcon, Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { assetListQueryFromSearch } from '@/products/helpdesk/features/assets/asset-list-path';
import { AssetsFilters } from '@/products/helpdesk/features/assets/assets-filters';
import { AssetsTable } from '@/products/helpdesk/features/assets/assets-table';
import { useAssetLookups, useAssets } from '@/products/helpdesk/features/assets/use-assets';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { ApiRequestError } from '@/lib/api';

const EMPTY_QUERY: AssetListQuery = {
  page: 1,
  pageSize: 25,
  sortBy: 'name',
  sortOrder: 'asc',
};

export function AssetsPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.ASSETS_WRITE);
  const lookups = useAssetLookups();
  const [searchParams] = useSearchParams();
  const fromUrl = assetListQueryFromSearch(searchParams.toString());
  const [searchInput, setSearchInput] = useState(fromUrl?.q ?? '');
  const [query, setQuery] = useState<AssetListQuery>({ ...EMPTY_QUERY, ...fromUrl });
  const assets = useAssets(query);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQuery((current) => {
        const next = searchInput.trim() || undefined;
        if (current.q === next) {
          return current;
        }
        return { ...current, q: next, page: 1 };
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const totalPages = assets.data
    ? Math.max(1, Math.ceil(assets.data.total / assets.data.pageSize))
    : 1;

  function patchQuery(patch: Partial<AssetListQuery>) {
    setQuery((current) => ({ ...current, ...patch }));
  }

  function onSortChange(sortBy: AssetListSortBy) {
    setQuery((current) => ({
      ...current,
      sortBy,
      sortOrder: current.sortBy === sortBy && current.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Assets</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Geräte, Lizenzen und Standorte. Tickets können mit einem oder mehreren Assets verknüpft
            werden.
          </p>
        </div>
        {canWrite && (
          <Button asChild>
            <Link to="/helpdesk/assets/neu">Neues Asset</Link>
          </Button>
        )}
      </div>

      <AssetsFilters
        query={query}
        lookups={lookups.data}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onChange={patchQuery}
      />

      {assets.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Assets werden geladen …
        </p>
      )}

      {assets.isError && (
        <p role="alert" className="text-sm text-destructive">
          {assets.error instanceof ApiRequestError
            ? assets.error.message
            : 'Die Asset-Liste konnte nicht geladen werden.'}
        </p>
      )}

      {assets.data && (
        <>
          <div className="overflow-hidden rounded-md border">
            <AssetsTable items={assets.data.items} query={query} onSortChange={onSortChange} />
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {assets.data.total === 0
                ? 'Keine Einträge'
                : `${assets.data.total} Assets · Seite ${assets.data.page} von ${totalPages}`}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={query.page <= 1}
                onClick={() => patchQuery({ page: query.page - 1 })}
              >
                <ChevronLeftIcon />
                Zurück
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={query.page >= totalPages}
                onClick={() => patchQuery({ page: query.page + 1 })}
              >
                Weiter
                <ChevronRightIcon />
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
